import Container, { Service } from "typedi";
import { RPCChannel } from "../../../shared/RPC/RPCChannel";
import { AgentServerRPCCommandMap } from "../../../shared/RPC/AgentServerRPCCommandMap";
import { AgentRPCCommandMap } from "../../../shared/RPC/AgentRPCCommandMap";
import { AgentRPCCommands } from "../../../shared/RPC/AgentRPCCommands";
import { AgentServerAPI } from "../../../shared/RPC/AgentServerAPI";
import { RPCMessage } from "../../../shared/RPC/RPCMessage";
import { StudentRulesCache } from "./state/StudentRulesCache";
import { DeviceStatusCache } from "./state/DeviceStatusCache";
import { UrlEvaluator } from "./state/UrlEvaluator";
import { DesktopSnapshotAggregator } from "./snapshot/DesktopSnapshotAggregator";

const HEALTH_CHECK_INTERVAL_MS = 10_000;
const HEALTH_CHECK_TIMEOUT_MS = 10_000;

type Sender = (message: RPCMessage) => void;

/**
 * Leader-side wrapper around the bidirectional `RPCChannel`.
 *
 * Mirrors the responsibilities of `AgentSession.start` in the chrome agent:
 *   1. Fetch initial state (session id, rules, device status) after connect.
 *   2. Wire the category lookup so UrlEvaluator can call the server.
 *   3. Health-check loop to detect a dead socket.
 */
@Service()
export class ServerSession {
    private channel: RPCChannel<AgentServerRPCCommandMap, AgentRPCCommandMap> | null = null;
    private api: AgentServerAPI | null = null;
    private healthInterval: ReturnType<typeof setInterval> | null = null;
    private disconnect: (() => void) | null = null;

    constructor(
        private readonly rulesCache: StudentRulesCache,
        private readonly deviceStatusCache: DeviceStatusCache,
        private readonly urlEvaluator: UrlEvaluator,
        private readonly desktopSnapshotAggregator: DesktopSnapshotAggregator
    ) {}

    getApi(): AgentServerAPI | null {
        return this.api;
    }

    async start(send: Sender, disconnect: () => void): Promise<void> {
        const commands = Container.get(AgentRPCCommands);
        this.channel = new RPCChannel<AgentServerRPCCommandMap, AgentRPCCommandMap>(commands);
        this.api = new AgentServerAPI(this.channel as any);
        this.channel.start(send);
        this.disconnect = disconnect;

        this.urlEvaluator.setCategoryLookup((url) => this.api!.queryUrlEvaluationByWebCategory(url));

        try {
            const sessionId = await this.api.getSessionId();
            this.desktopSnapshotAggregator.setSessionId(sessionId);
        } catch (err) {
            console.warn("[server-session] getSessionId failed", err);
        }

        try {
            const rules = await this.api.getStudentRules();
            this.rulesCache.set(rules);
        } catch (err) {
            console.warn("[server-session] getStudentRules failed", err);
        }

        try {
            const deviceStatus = await this.api.getStudentDeviceStatus();
            this.deviceStatusCache.set(deviceStatus);
        } catch (err) {
            console.warn("[server-session] getStudentDeviceStatus failed", err);
        }

        this.startHealthCheck();
    }

    async handleMessage(message: RPCMessage): Promise<void> {
        await this.channel?.handleMessage(message);
    }

    async close(): Promise<void> {
        this.stopHealthCheck();
        this.channel?.close();
        this.channel = null;
        this.api = null;
        this.disconnect = null;
        this.urlEvaluator.setCategoryLookup(null);
    }

    private startHealthCheck(): void {
        this.stopHealthCheck();
        this.healthInterval = setInterval(async () => {
            if (!this.api) return;
            try {
                await Promise.race([
                    this.api.getHealth(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error("health timeout")), HEALTH_CHECK_TIMEOUT_MS))
                ]);
            } catch (err) {
                console.warn("[server-session] health check failed, disconnecting", err);
                this.disconnect?.();
            }
        }, HEALTH_CHECK_INTERVAL_MS);
    }

    private stopHealthCheck(): void {
        if (this.healthInterval) {
            clearInterval(this.healthInterval);
            this.healthInterval = null;
        }
    }
}
